'use client';

import type {
  PortalActionTokenState,
  PortalInvitationValidationResult,
  PortalPasswordMutationApiResponse,
} from 'src/auth/types';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';

import { paths } from 'src/routes/paths';

import { EmailInboxIcon } from 'src/assets/icons';

import { Form, Field } from 'src/components/hook-form';

import { FormHead } from 'src/auth/components/form-head';
import { portalSetInitialPasswordSchema } from 'src/auth/schema';
import { FormReturnLink } from 'src/auth/components/form-return-link';
import { PORTAL_PASSWORD_MIN_LENGTH, PORTAL_PASSWORD_MIN_LENGTH_HINT } from 'src/auth/constants';
import {
  getTokenStateMessage,
  getAuthRateLimitedMessage,
  getAuthUnavailableMessage,
} from 'src/auth/error-message';

type PortalActivateAccountViewProps = {
  token?: string | null;
  initialValidation?: PortalInvitationValidationResult | null;
  isUnavailable?: boolean;
};

export function PortalActivateAccountView({
  token = null,
  initialValidation = null,
  isUnavailable = false,
}: PortalActivateAccountViewProps) {
  const initialState = initialValidation?.state ?? null;
  const initialTokenStateMessage =
    initialState && initialState !== 'READY' ? getTokenStateMessage('activation', initialState) : null;

  if (initialTokenStateMessage) {
    return (
      <>
        <FormHead
          icon={<EmailInboxIcon />}
          title={initialTokenStateMessage.title}
          description={initialTokenStateMessage.description}
        />

        <Alert severity="warning" sx={{ mb: 3 }}>
          {initialTokenStateMessage.description}
        </Alert>

        <FormReturnLink href={paths.login} label="Terug naar inloggen" />
      </>
    );
  }

  if (!token) {
    return (
      <>
        <FormHead
          icon={<EmailInboxIcon />}
          title="Account activeren"
          description="Accountactivatie verloopt via de uitnodigingslink uit je e-mail."
        />

        <Alert severity="info" sx={{ mb: 3 }}>
          Open de meest recente activatiemail van VlamSafe om je account te activeren en je
          initiële wachtwoord in te stellen.
        </Alert>

        <FormReturnLink href={paths.login} label="Terug naar inloggen" />
      </>
    );
  }

  if (isUnavailable) {
    return (
      <>
        <FormHead
          icon={<EmailInboxIcon />}
          title="Activatie tijdelijk niet beschikbaar"
          description="We kunnen deze activatielink momenteel niet controleren."
        />

        <Alert severity="error" sx={{ mb: 3 }}>
          {getAuthUnavailableMessage('activation')} Probeer het later opnieuw of neem contact op
          met VlamSafe.
        </Alert>

        <FormReturnLink href={paths.login} label="Terug naar inloggen" />
      </>
    );
  }

  const readyValidation = initialValidation?.state === 'READY' ? initialValidation : null;

  return <PortalActivateAccountReadyMode token={token} initialValidation={readyValidation} />;
}

function PortalActivateAccountReadyMode({
  token,
  initialValidation,
}: {
  token: string;
  initialValidation: Extract<PortalInvitationValidationResult, { state: 'READY' }> | null;
}) {
  const [serverError, setServerError] = useState<string | null>(null);
  const [tokenState, setTokenState] = useState<Exclude<PortalActionTokenState, 'READY'> | null>(null);

  const methods = useForm({
    resolver: zodResolver(portalSetInitialPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const {
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);

    const response = await fetch('/api/portal-auth/set-initial-password/', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      referrerPolicy: 'no-referrer',
      body: JSON.stringify({ token, ...values }),
    });

    const result = (await response.json()) as PortalPasswordMutationApiResponse;

    if (result.ok) {
      window.location.assign(result.redirectTo);
      return;
    }

    if (result.error === 'validation_error' && result.fieldErrors) {
      Object.entries(result.fieldErrors).forEach(([field, message]) => {
        if (message) {
          setError(field as 'password' | 'confirmPassword', { message, type: 'server' });
        }
      });
      return;
    }

    if (result.error === 'password_policy') {
      setError('password', {
        message: result.message || 'Het gekozen wachtwoord voldoet niet aan de vereisten.',
        type: 'server',
      });
      return;
    }

    if (result.error === 'rate_limited') {
      setServerError(result.message || getAuthRateLimitedMessage('activation'));
      return;
    }

    if (result.error === 'token_state' && result.tokenState) {
      setTokenState(result.tokenState);
      return;
    }

    setServerError(getAuthUnavailableMessage('activation'));
  });

  const tokenStateMessage = tokenState ? getTokenStateMessage('activation', tokenState) : null;

  if (tokenStateMessage) {
    return (
      <>
        <FormHead
          icon={<EmailInboxIcon />}
          title={tokenStateMessage.title}
          description={tokenStateMessage.description}
        />

        <Alert severity="warning" sx={{ mb: 3 }}>
          {tokenStateMessage.description}
        </Alert>

        <FormReturnLink href={paths.login} label="Terug naar inloggen" />
      </>
    );
  }

  return (
    <>
      <FormHead
        icon={<EmailInboxIcon />}
        title="Account activeren"
        description="Kies een wachtwoord om je portaalaccount te activeren."
      />

      {serverError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {serverError}
        </Alert>
      )}

      {initialValidation?.email && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Je activeert portaaltoegang voor <strong>{initialValidation.email}</strong>.
        </Alert>
      )}

      <Form methods={methods} onSubmit={onSubmit}>
        <Stack spacing={3}>
          <Field.Text
            name="password"
            label="Nieuw wachtwoord"
            placeholder={`Minimaal ${PORTAL_PASSWORD_MIN_LENGTH} tekens`}
            helperText={PORTAL_PASSWORD_MIN_LENGTH_HINT}
            type="password"
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Field.Text
            name="confirmPassword"
            label="Herhaal wachtwoord"
            placeholder="Herhaal je wachtwoord"
            type="password"
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Button
            fullWidth
            color="primary"
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            loadingIndicator="Account activeren..."
          >
            Account activeren
          </Button>
        </Stack>
      </Form>

      <FormReturnLink href={paths.login} label="Terug naar inloggen" />
    </>
  );
}
